import { Component, ChangeDetectionStrategy } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs';
import { EventOverview } from 'src/app/core-module/models/Events/event-overview';
import { GetEventResult } from 'src/app/core-module/models/Events/get-event-result';
import { EventsService } from 'src/app/core-module/services/events.service';
import { EventOverviewPanelComponent } from '../events/event-overview-panel/event-overview-panel.component';
import { AuditResultDetailComponent } from '../results-home/audit-result-detail/audit-result-detail.component';
import { SecureComponentsModule } from '../secure-components/secure-components.module';
import { AuditResultDetail } from 'src/app/core-module/models/Audits/AuditResults/audit-result-detail';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { EventActionAddComponent } from './event-action-add/event-action-add.component';
import { ComplianceAction } from 'src/app/core-module/models/Audits/compliance-action';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { ComplianceActionService } from 'src/app/core-module/services/compliance-action.service';
import { ActionUserRequest } from 'src/app/core-module/models/Compliance-Actions/action-user-request';
import { UserService } from 'src/app/core-module/services/user.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { UtilService } from 'src/app/core-module/services/util.service';
import { EventActionsListComponent } from './event-actions-list/event-actions-list.component';
import { EventCommentsComponent } from './event-comments/event-comments.component';
import { BasicAction } from 'src/app/core-module/models/Audits/AuditResults/basic-action';
import { EventComment } from 'src/app/core-module/models/Events/event-comment';
import { FormsModule } from '@angular/forms';
import { WorkflowTemplate } from 'src/app/core-module/models/Events/workflow-template';
import { WorkflowActionTemplate } from 'src/app/core-module/models/Events/workflow-action-template';
import { ApplyEventWorkflowAction, ApplyEventWorkflowRequest } from 'src/app/core-module/models/Events/apply-event-workflow-request';
import { AuditResultsService } from 'src/app/core-module/services/audit-results.service';
import { EventStatusItem } from 'src/app/core-module/models/Events/event-status-item';
import { EventAttachmentsComponent, EventAttachmentVM } from './event-attachments/event-attachments.component';
import { QuestionAttachment } from 'src/app/core-module/models/Audits/question-attachment';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { EventHistory } from 'src/app/core-module/models/Events/event-history';
import { EventHistoryTimelineComponent } from './event-history-timeline/event-history-timeline.component';
import { FlattenedQuestionHolder } from 'src/app/core-module/models/Audits/flattened-question-holder';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { CustomerTermPipe, CustomerTextPipe } from 'src/app/shared-module/pipes/customer-term.pipe';

@Component({
  selector: 'app-event-page',
  imports: [CommonModule,FormsModule,EventOverviewPanelComponent,SecureComponentsModule,TabsModule,EventActionAddComponent,EventActionsListComponent,EventCommentsComponent,EventAttachmentsComponent,EventHistoryTimelineComponent,ConfirmPopupModule,CustomerTermPipe,CustomerTextPipe],
  standalone:true,
  providers: [ConfirmationService],
  templateUrl: './event-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './event-page.component.scss',
})
export class EventPageComponent {

  public EventID: number;

  public loading: boolean = true;

  public overview:EventOverview;
  public details:AuditResultDetail;
  public actionUsers: BasicUser[] = [];
  public savingAction: boolean = false;
  public loadingActionUsers: boolean = false;
  public newActionResetToken: number = 0;
  public outstandingActions: BasicAction[] = [];
  public loadingActions: boolean = false;
  public additionalAuditResults: AuditResultDetail[] = [];
  public loadingAdditionalAudits: boolean = false;
  public activeTab: string = 'overview';
  public comments: EventComment[] = [];
  public savingComment: boolean = false;
  public loadingComments: boolean = false;
  public commentError: string = '';
  public workflows: WorkflowTemplate[] = [];
  public loadingWorkflows: boolean = false;
  public workflowActions: WorkflowActionTemplate[] = [];
  public loadingWorkflowActions: boolean = false;
  public selectedWorkflowId: number = -1;
  public workflowSelections: Record<number, ApplyEventWorkflowAction> = {};
  public applyingWorkflow: boolean = false;
  public workflowError: string = '';
  public eventStatuses: EventStatusItem[] = [];
  public loadingStatuses: boolean = false;
  public savingStatus: boolean = false;
  public statusError: string = '';
  public selectedStatusId: number | null = null;
  public eventAttachments: EventAttachmentVM[] = [];
  public uploadingEventAttachment: boolean = false;
  public eventAttachmentError: string = '';
  private eventAttachmentRemoveGuid: string | null = null;
  public eventHistory: EventHistory[] = [];
  public loadingEventHistory: boolean = false;
  public eventHistoryError: string = '';
  public actionAuditOptions: { AuditID: number; AuditName: string }[] = [];
  public loadingActionAudits: boolean = false;

  get selectedWorkflowInfo(): WorkflowTemplate | undefined{
    return this.workflows.find(w => w.WorkflowID === this.selectedWorkflowId);
  }

  get openActions(): BasicAction[]{
    return (this.outstandingActions || []).filter(a => !a.Resolved);
  }

  get resolvedActions(): BasicAction[]{
    return (this.outstandingActions || []).filter(a => a.Resolved);
  }

  get overdueCount(): number{
    const today = this.util.stripTime(new Date());
    return this.openActions.filter(a => a.ActionDeadline && this.util.stripTime(a.ActionDeadline) < today).length;
  }

  get dueTodayCount(): number{
    const today = this.util.stripTime(new Date());
    return this.openActions.filter(a => a.ActionDeadline && this.util.stripTime(a.ActionDeadline).toString() === today.toString()).length;
  }

  get topDueActions(): BasicAction[]{
    return [...this.openActions]
      .filter(a => !!a.ActionDeadline)
      .sort((a,b) => (a.ActionDeadline?.getTime() || 0) - (b.ActionDeadline?.getTime() || 0))
      .slice(0,3);
  }

  get pendingAuditActions(): BasicAction[]{
    return this.openActions.filter(a => (a.ActionVariationID ?? 1) === 2);
  }

  private toVm(list: QuestionAttachment[] = []): EventAttachmentVM[]{
    return (list || []).map(a => ({
      FileName: a.FileName,
      GUID: a.GUID,
      MimeType: a.MimeType,
      Length: a.Length
    }));
  }

  constructor(
    private route:ActivatedRoute,
    private router:Router,
    private Events:EventsService,
    private CAS: ComplianceActionService,
    private userHelperService: UserService,
    private toast: ToastrService,
    private util: UtilService,
    private auditResults: AuditResultsService,
    private confirmationService: ConfirmationService,
    private userManagement: UserManagementService
    ){
    if (!route.snapshot.paramMap.has('id')){
      this.router.navigate(['/events']);
    }

    this.EventID = Number(route.snapshot.paramMap.get('id')) ?? -1;
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab){
      this.activeTab = tab;
    }

    this.Events.getEvent(this.EventID).pipe(
      tap((res:GetEventResult) => {
        this.loading = false;
        console.log('event',res);
        if (res.IsError){
          //show error toastr
          return;
        }
        this.overview = res.EventOverview;
        this.details = res.AuditResult;
        this.selectedStatusId = this.overview?.EventStatusID ?? null;
        this.loadEventActionUsers();
        this.loadEventActions();
        this.loadComments();
        this.loadEventWorkflows();
        this.loadEventStatuses();
        this.loadEventAttachments();
        this.loadEventHistory();
        this.loadActionAudits();
      })
    ).subscribe();

  }

  loadEventActionUsers(){
    if(!this.overview){
      return;
    }
    this.loadingActionUsers = true;
    const req = new ActionUserRequest({ LocationID: this.overview.CustomerLocationID, EventID: this.EventID });
    this.CAS.getActionUsers(req).pipe(
      tap((users:BasicUser[]) => {
        this.loadingActionUsers = false;
        this.actionUsers = this.userHelperService.sortUsers(users);
        if (!this.actionUsers.some(u => u.UserID === -1)){
          const placeholder = new BasicUser({ 'UserID': -1, 'DisplayName': 'Please Select...', 'Seq': 0, 'UserTypeID': 0 }) as any;
          placeholder.Disabled = true;
          this.actionUsers.unshift(placeholder);
        }
      })
    ).subscribe({
      error: () => {
        this.loadingActionUsers = false;
        this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to load action users for this event.' }));
      }
    });
  }

  onSaveEventAction(action: ComplianceAction){
    this.savingAction = true;
    action.EventID = this.EventID;
    action.LocationID = this.overview.CustomerLocationID;
    action.ActionOwnerTypeID = action.ActionOwner?.UserTypeID ?? action.ActionOwnerTypeID;
    action.ActionDeadline = this.util.removeTimezoneFromDate(action.ActionDeadline);
    this.CAS.saveEventAction(action).pipe(
      tap(res => {
        this.savingAction = false;
        if (res.IsError){
          this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to create action, please try again.' }));
          return;
        }
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Action created.' }));
        this.newActionResetToken++;
        this.loadEventActions();
        this.loadEventHistory();
      })
    ).subscribe({
      error: () => {
        this.savingAction = false;
        this.toast.addToastr(new Toastr({ Type: 3, Message: 'Unable to create action, please try again.' }));
      }
    });
  }

  onCancelEventAction(){
    this.newActionResetToken++;
  }

  loadEventActions(){
    this.loadingActions = true;
    this.CAS.getEventActions(this.EventID, false, -1, 0, -1, true).pipe(
      tap((actions:BasicAction[]) => {
        this.loadingActions = false;
        this.outstandingActions = actions;
        this.loadAdditionalAuditResults();
      })
    ).subscribe({
      error: () => {
        this.loadingActions = false;
        this.outstandingActions = [];
      }
    });
  }

  private loadAdditionalAuditResults(){
    const primaryAuditResultId = this.details?.AuditResultID ?? -1;
    const auditActionIds = Array.from(new Set((this.outstandingActions || [])
      .filter(a => (a.ActionTypeID === 2 || a.ActionTypeID === 0 || a.ActionTypeID === undefined) && a.ActionVariationID === 2 && (a.AuditResultID ?? 0) > 0)
      .map(a => a.AuditResultID as number)
      .filter(id => id > 0 && id !== primaryAuditResultId)));
    if (auditActionIds.length === 0){
      this.additionalAuditResults = [];
      return;
    }
    this.loadingAdditionalAudits = true;
    import('rxjs').then(rx => {
      rx.forkJoin(auditActionIds.map(id => this.auditResults.getAuditResultDetail(id))).subscribe({
        next: results => {
          this.loadingAdditionalAudits = false;
          this.additionalAuditResults = results || [];
        },
        error: () => {
          this.loadingAdditionalAudits = false;
          this.additionalAuditResults = [];
        }
      });
    });
  }

  openActionDetail(actionId:number){
    if (actionId && actionId !== -1){
      this.router.navigate(['/actionv2'], { queryParams: { id: actionId, from: 'event', eventId: this.EventID }});
    }
  }

  addComment(note: string){
    const trimmed = note?.trim();
    if (!trimmed){
      this.commentError = 'Comment cannot be empty.';
      return;
    }
    this.commentError = '';
    this.savingComment = true;
    this.Events.addEventComment(this.EventID, trimmed).pipe(
      tap(res => {
        this.savingComment = false;
        if (res?.IsError){
          this.commentError = 'Unable to add comment. Please try again.';
          return;
        }
        this.loadComments();
        this.loadEventHistory();
      })
    ).subscribe({
      error: () => {
        this.savingComment = false;
        this.commentError = 'Unable to add comment. Please try again.';
      }
    });
  }

  private loadComments(){
    this.loadingComments = true;
    this.Events.getEventComments(this.EventID).pipe(
      tap((comments: EventComment[]) => {
        this.loadingComments = false;
        this.comments = comments ?? [];
      })
    ).subscribe({
      error: () => {
        this.loadingComments = false;
        this.comments = [];
      }
    });
  }

  loadEventWorkflows(){
    if (!this.EventID){
      return;
    }
    this.loadingWorkflows = true;
    this.workflowError = '';
    this.Events.getEventWorkflows(this.EventID).subscribe({
      next: list => {
        this.loadingWorkflows = false;
        this.workflows = list || [];
        if (this.workflows.length > 0){
          this.selectWorkflow(this.workflows[0].WorkflowID);
        }
      },
      error: () => {
        this.loadingWorkflows = false;
        this.workflows = [];
        this.workflowError = 'Unable to load workflows.';
      }
    });
  }

  selectWorkflow(workflowId: number){
    if (!workflowId || workflowId === this.selectedWorkflowId){
      return;
    }
    this.selectedWorkflowId = workflowId;
    this.loadWorkflowActions(workflowId);
  }

  private buildDefaultSelection(){
    this.workflowSelections = {};
    (this.workflowActions || []).forEach(act => {
      const entry = new ApplyEventWorkflowAction({
        WorkflowActionID: act.WorkflowActionID,
        Include: act.IsMandatory || true,
        DueInDays: act.DefaultDueInDays ?? 0,
        OwnerUserID: act.DefaultOwnerUserID ?? null,
        OwnerTypeID: act.DefaultOwnerTypeID ?? null
      });
      this.workflowSelections[act.WorkflowActionID] = entry;
    });
  }

  private loadWorkflowActions(workflowId: number){
    if (!workflowId){
      return;
    }
    this.loadingWorkflowActions = true;
    this.workflowError = '';
    this.Events.getEventWorkflowActions(this.EventID, workflowId).subscribe({
      next: actions => {
        this.loadingWorkflowActions = false;
        this.workflowActions = actions || [];
        this.buildDefaultSelection();
      },
      error: () => {
        this.loadingWorkflowActions = false;
        this.workflowActions = [];
        this.workflowError = 'Unable to load workflow actions.';
      }
    });
  }

  toggleActionSelection(actionId: number, include: boolean){
    const sel = this.workflowSelections[actionId];
    if (!sel){ return; }
    sel.Include = include;
  }

  updateActionDue(actionId: number, value: number){
    const sel = this.workflowSelections[actionId];
    if (!sel){ return; }
    sel.DueInDays = value ?? 0;
  }

  updateActionOwner(actionId: number, userId: number | null, userTypeId: number | null){
    const sel = this.workflowSelections[actionId];
    if (!sel){ return; }
    sel.OwnerUserID = userId ?? null;
    sel.OwnerTypeID = userTypeId ?? null;
  }

  ownerTypeFor(userId: number | null): number | null{
    if (userId === null || userId === undefined){
      return null;
    }
    const user = this.actionUsers.find(u => u.UserID === userId);
    return user?.UserTypeID ?? null;
  }

  applySelectedWorkflow(){
    if (!this.selectedWorkflowId || this.selectedWorkflowId === -1){
      this.workflowError = 'Please select a workflow.';
      return;
    }
    const payload = new ApplyEventWorkflowRequest({
      EventID: this.EventID,
      WorkflowID: this.selectedWorkflowId,
      Actions: Object.values(this.workflowSelections || {})
    });

    // basic client validation for mandatory
    const mandatoryMissing = (this.workflowActions || []).some(a => a.IsMandatory && !this.workflowSelections[a.WorkflowActionID]?.Include);
    if (mandatoryMissing){
      this.workflowError = 'Mandatory actions must be included.';
      return;
    }

    this.workflowError = '';
    this.applyingWorkflow = true;
    this.Events.applyEventWorkflow(payload).subscribe({
      next: res => {
        this.applyingWorkflow = false;
        if (res?.IsError){
          this.workflowError = res.ErrorMessage || 'Unable to apply workflow.';
          return;
        }
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Workflow applied.' }));
        this.loadEventActions();
        this.resetWorkflowPanel();
        this.loadEventHistory();
      },
      error: () => {
        this.applyingWorkflow = false;
        this.workflowError = 'Unable to apply workflow.';
      }
    });
  }

  loadEventStatuses(){
    this.loadingStatuses = true;
    this.statusError = '';
    this.Events.getEventStatuses().subscribe({
      next: list => {
        this.loadingStatuses = false;
        this.eventStatuses = list || [];
        if (this.selectedStatusId === null){
          this.selectedStatusId = this.overview?.EventStatusID ?? null;
        }
      },
      error: () => {
        this.loadingStatuses = false;
        this.eventStatuses = [];
        this.statusError = 'Unable to load event statuses.';
      }
    });
  }

  saveEventStatus(){
    if (!this.overview){
      return;
    }
    if (this.selectedStatusId === null){
      this.statusError = 'Please select a status.';
      return;
    }
    if (this.selectedStatusId === this.overview.EventStatusID){
      this.statusError = '';
      return;
    }
    this.statusError = '';
    this.savingStatus = true;
    this.Events.updateEventStatus(this.EventID, this.selectedStatusId).subscribe({
      next: res => {
        this.savingStatus = false;
        if (res?.IsError){
          this.statusError = res.ErrorMessage || 'Unable to update status.';
          return;
        }
        this.overview.EventStatusID = this.selectedStatusId ?? this.overview.EventStatusID;
        const match = this.eventStatuses.find(s => s.EventStatusID === this.selectedStatusId);
        this.overview.EventStatus = match?.EventStatusDescription ?? this.overview.EventStatus;
        this.toast.addToastr(new Toastr({ Type: 2, Message: 'Event status updated.' }));
        this.loadEventHistory();
      },
      error: () => {
        this.savingStatus = false;
        this.statusError = 'Unable to update status.';
      }
    });
  }

  private resetWorkflowPanel(){
    this.selectedWorkflowId = -1;
    this.workflowActions = [];
    this.workflowSelections = {};
  }

  loadEventAttachments(){
    if (!this.EventID || this.EventID === -1){
      return;
    }
    this.eventAttachmentError = '';
    this.Events.getEventAttachments(this.EventID).subscribe({
      next: (list: QuestionAttachment[]) => {
        this.eventAttachments = this.toVm(list);
        this.loadEventHistory();
      },
      error: () => {
        this.eventAttachments = [];
        this.eventAttachmentError = 'Unable to load attachments.';
      }
    });
  }

  loadEventHistory(){
    if (!this.EventID || this.EventID === -1){
      return;
    }
    this.eventHistoryError = '';
    this.loadingEventHistory = true;
    this.Events.getEventHistory(this.EventID).subscribe({
      next: (hist: EventHistory[]) => {
        this.loadingEventHistory = false;
        this.eventHistory = (hist || []).map(h => ({
          ...h,
          DateOfChange: h?.DateOfChange ? new Date(h.DateOfChange) : h.DateOfChange
        }));
      },
      error: () => {
        this.loadingEventHistory = false;
        this.eventHistoryError = 'Unable to load history.';
      }
    });
  }

  private loadActionAudits(){
    if (!this.overview?.CustomerLocationID){
      this.actionAuditOptions = [];
      return;
    }
    this.loadingActionAudits = true;
    this.userManagement.getLocationAudits(this.overview.CustomerLocationID).subscribe({
      next: audits => {
        this.loadingActionAudits = false;
        const options = (audits || [])
          .filter(a => a?.AuditID && a.AuditID > 0)
          .map(a => ({ AuditID: a.AuditID, AuditName: a.AuditName || `Audit ${a.AuditID}` }))
          .sort((a,b) => a.AuditName.localeCompare(b.AuditName));
        this.actionAuditOptions = options;
      },
      error: () => {
        this.loadingActionAudits = false;
        this.actionAuditOptions = [];
      }
    });
  }

  onUploadEventAttachment(file: File){
    if (!file || !this.EventID || this.EventID === -1){
      return;
    }
    this.eventAttachmentError = '';
    this.uploadingEventAttachment = true;
    this.Events.uploadEventAttachment(this.EventID, file).subscribe({
      next: (event: any) => {
        if (event?.type === HttpEventType.Response){
          this.uploadingEventAttachment = false;
          const body = event?.body ?? event;
          if (body?.IsError){
            this.eventAttachmentError = 'Unable to upload attachment. Please try again.';
            return;
          }
          this.loadEventAttachments();
        }
      },
      error: () => {
        this.uploadingEventAttachment = false;
        this.eventAttachmentError = 'Unable to upload attachment. Please try again.';
      }
    });
  }

  onDownloadEventAttachment(guid: string){
    if (!guid){ return; }
    this.Events.downloadEventAttachment(guid).subscribe({
      next: (res:any) => {
        const blob = res?.body ?? res;
        if (!blob){
          return;
        }
        const headers = res?.headers;
        const contentDisposition = headers?.get?.('content-disposition') || headers?.get?.('Content-Disposition') || '';
        let filename = this.eventAttachments.find(a => a.GUID === guid)?.FileName || 'attachment';
        const starMatch = /filename\\*\\s*=\\s*(?:UTF-8''|)([^;]+)/i.exec(contentDisposition);
        if (starMatch && starMatch[1]){
          const cleaned = starMatch[1].trim().replace(/(^['\"]|['\"]$)/g, '');
          filename = decodeURIComponent(cleaned);
        } else {
          const stdMatch = /filename\\s*=\\s*\"?([^\";]+)\"?/i.exec(contentDisposition);
          if (stdMatch && stdMatch[1]){
            filename = stdMatch[1];
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {}
    });
  }

  onRemoveEventAttachment(payload: { guid: string; event: Event }){
    if (!payload?.guid){ return; }
    this.eventAttachmentRemoveGuid = payload.guid;
    this.confirmationService.confirm({
      target: payload.event?.target as any,
      key: 'eventAttachmentConfirm',
      header: 'Remove attachment',
      message: 'Are you sure you want to remove this attachment?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      acceptButtonStyleClass: 'confirm-remove-btn',
      rejectButtonStyleClass: 'confirm-keep-btn',
      accept: () => this.removeEventAttachment(),
      reject: () => this.confirmationService.close(),
      styleClass: 'confirm-popup-danger'
    } as any);
  }

  onConfirmReject(confirmation: any){
    confirmation?.rejectEvent?.emit();
    this.confirmationService.close();
  }

  onConfirmAccept(confirmation: any){
    confirmation?.acceptEvent?.emit();
    this.confirmationService.close();
  }

  removeEventAttachment(){
    if (!this.eventAttachmentRemoveGuid || !this.EventID || this.EventID === -1){
      return;
    }
    const guid = this.eventAttachmentRemoveGuid;
    this.eventAttachmentRemoveGuid = null;
    this.eventAttachmentError = '';
    this.uploadingEventAttachment = true;
    this.Events.deleteEventAttachment(this.EventID, guid).subscribe({
      next: (res: BasicResult) => {
        this.uploadingEventAttachment = false;
        if (res?.IsError){
          this.eventAttachmentError = 'Unable to remove attachment. Please try again.';
          return;
        }
        this.eventAttachments = this.eventAttachments.filter(a => a.GUID !== guid);
        this.loadEventAttachments();
        this.loadEventHistory();
      },
      error: () => {
        this.uploadingEventAttachment = false;
        this.eventAttachmentError = 'Unable to remove attachment. Please try again.';
      }
    });
  }

}
